import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../../core/auth/auth.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent {
  private readonly fb = inject(FormBuilder);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);

  readonly loading = signal(false);
  readonly errorMessage = signal('');
  readonly showPassword = signal(false);
  readonly mfaStep = signal(false);
  readonly challengeId = signal('');

  readonly loginForm = this.fb.nonNullable.group({
    login: ['', Validators.required],
    password: ['', Validators.required]
  });

  readonly mfaForm = this.fb.nonNullable.group({
    code: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]]
  });

  submitLogin(): void {
    this.errorMessage.set('');

    if (this.loginForm.invalid || this.loading()) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.loading.set(true);
    const credentials = this.loginForm.getRawValue();

    this.auth.login(credentials.login.trim(), credentials.password).subscribe({
      next: (response) => {
        this.loading.set(false);

        if (response.mfaRequired && response.challengeId) {
          this.challengeId.set(response.challengeId);
          this.mfaStep.set(true);
          return;
        }

        if (response.authenticated) {
          void this.goToAuthorizedPortal(response.redirectRoute);
          return;
        }

        this.errorMessage.set(this.failureMessage(response.failureCode));
      },
      error: (error: HttpErrorResponse) => {
        this.loading.set(false);
        this.errorMessage.set(this.httpErrorMessage(error));
      }
    });
  }

  submitMfa(): void {
    this.errorMessage.set('');

    if (this.mfaForm.invalid || this.loading() || !this.challengeId()) {
      this.mfaForm.markAllAsTouched();
      return;
    }

    this.loading.set(true);

    this.auth
      .verifyMfa(this.challengeId(), this.mfaForm.getRawValue().code)
      .subscribe({
        next: (response) => {
          this.loading.set(false);

          if (response.authenticated) {
            void this.goToAuthorizedPortal(response.redirectRoute);
            return;
          }

          this.errorMessage.set(this.failureMessage(response.failureCode));
        },
        error: (error: HttpErrorResponse) => {
          this.loading.set(false);
          this.errorMessage.set(this.httpErrorMessage(error));
        }
      });
  }

  togglePassword(): void {
    this.showPassword.update((value) => !value);
  }

  backToCredentials(): void {
    this.mfaStep.set(false);
    this.challengeId.set('');
    this.mfaForm.reset();
    this.errorMessage.set('');
  }

  private async goToAuthorizedPortal(route?: string): Promise<void> {
    const allowedRoutes = [
      '/app/niotech',
      '/app/admin',
      '/app/technician',
      '/app/services'
    ];

    await this.router.navigateByUrl(
      route && allowedRoutes.includes(route) ? route : '/app'
    );
  }

  private failureMessage(code?: string): string {
    if (code === 'NO_AUTHORIZED_PORTAL') {
      return 'Tu cuenta esta activa, pero no tiene un portal autorizado. Contacta a tu administrador.';
    }

    return 'No pudimos iniciar sesion. Revisa tus datos e intenta nuevamente.';
  }

  private httpErrorMessage(error: HttpErrorResponse): string {
    if (error.status === 0) {
      return 'No pudimos conectar con DeskPlus One. Verifica que el servicio este disponible.';
    }

    if (error.status === 429) {
      return 'Hay demasiados intentos. Espera un momento e intenta nuevamente.';
    }

    return 'No pudimos iniciar sesion. Revisa tus datos e intenta nuevamente.';
  }
}